import { accountStatus, instagramPermissions } from '../../../../../Model/InstagramUser';

const LEAD_NAME_PLACEHOLDER_KEY = 'lead_name';
const LEAD_NAME_PLACEHOLDER = `{${LEAD_NAME_PLACEHOLDER_KEY}}`;
const PLACEHOLDER_PATTERN = /\{([^{}]*)\}/g;

/**
 * Reasons a coach cannot be sent Instagram outreach.
 * @enum {string}
 */
const IntegrationIssue = {
  NOT_CONNECTED: 'NOT_CONNECTED',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  MISSING_MESSAGE_PERMISSION: 'MISSING_MESSAGE_PERMISSION',
};

/**
 * Collect every placeholder in the template that is not {lead_name}.
 *
 * @param {string} template - The raw message template
 * @returns {string[]} The unrecognised placeholders, in the order they appear, deduplicated
 */
const getUnknownPlaceholders = (template) => {
  const matches = `${template || ''}`.match(PLACEHOLDER_PATTERN) || [];
  return [...new Set(matches.filter((match) => match !== LEAD_NAME_PLACEHOLDER))];
};

/**
 * Validate a setter message template. Save is blocked on an empty template or on any
 * placeholder the sender cannot resolve.
 *
 * @param {string} template - The raw message template
 * @returns {{ isValid: boolean, isEmpty: boolean, unknownPlaceholders: string[] }} The result
 */
const validateTemplate = (template) => {
  const isEmpty = !`${template || ''}`.trim();
  const unknownPlaceholders = isEmpty ? [] : getUnknownPlaceholders(template);

  return {
    isValid: !isEmpty && unknownPlaceholders.length === 0,
    isEmpty,
    unknownPlaceholders,
  };
};

/**
 * Work out why a coach cannot receive Instagram outreach, if at all. The setter can only send
 * from a connected account with a live session and message permission granted, so each of these
 * means no DM will ever go out for this coach however the settings below are configured.
 *
 * @param {object} [instagramUser] - The coach's Instagram user doc, if one exists
 * @returns {string | null} The matching IntegrationIssue, or null when outreach is possible
 */
const getIntegrationIssue = (instagramUser) => {
  if (!instagramUser) {
    return IntegrationIssue.NOT_CONNECTED;
  }
  if (instagramUser.accountStatus !== accountStatus.AUTHORIZED) {
    return IntegrationIssue.SESSION_EXPIRED;
  }
  const granted = instagramUser.grantedPermissions || [];
  if (!granted.includes(instagramPermissions.INSTAGRAM_BUSINESS_MANAGE_MESSAGES)) {
    return IntegrationIssue.MISSING_MESSAGE_PERMISSION;
  }
  return null;
};

export {
  LEAD_NAME_PLACEHOLDER,
  LEAD_NAME_PLACEHOLDER_KEY,
  IntegrationIssue,
  validateTemplate,
  getIntegrationIssue,
};
